import type { TransactionType } from '@/types/finance';

export type ImportType = 'pagar' | 'receber' | 'transferir' | 'fatura-cartao';

/**
 * Linha genérica de importação. Origem pode ser CSV/XLSX (Conta Azul) ou OFX (extrato bancário).
 * `type` é preenchido quando o parser sabe inferir (ex: OFX usa sinal de TRNAMT). Quando ausente,
 * o wizard usa o `ImportType` global escolhido pelo usuário no Step 1.
 */
export interface ImportRow {
  lineNumber: number;
  dueDate: Date;
  paymentDate?: Date;
  description: string;
  amount: number;
  categoryName?: string;
  costCenterName?: string;
  status: 'completed' | 'pending';
  notes?: string;
  type?: TransactionType;
  /** ID externo da transação na origem (FITID do OFX). Garante idempotência. */
  externalId?: string;
}

/** Alias para compatibilidade com código pré-OFX. */
export type ContaAzulRow = ImportRow;

export interface ParseError {
  line: number;
  reason: string;
}

/** Metadados do extrato OFX (banco, conta, período). Apenas informativo. */
export interface OfxMetadata {
  bankId?: string;
  accountId?: string;
  accountType?: string;
  startDate?: Date;
  endDate?: Date;
  currency?: string;
}

export interface ParseResult {
  rows: ImportRow[];
  errors: ParseError[];
  uniqueCategories: string[];
  uniqueCostCenters: string[];
  /** Presente apenas quando origem é OFX. */
  ofxMetadata?: OfxMetadata;
}

export type MappingDecision =
  | { kind: 'create' }
  | { kind: 'map'; targetId: string }
  | { kind: 'skip' };

export interface CategoryMapping {
  name: string;
  decision: MappingDecision;
}

export interface CostCenterMapping {
  name: string;
  decision: MappingDecision;
}

/**
 * Decisão por linha do OFX no PreviewStep:
 * - `create`: cria nova transação (default quando não há match)
 * - `reconcile`: marca transação pendente existente como Pago
 * - `reconcile_invoice`: fecha fatura completa do cartão (paga N transações de uma vez)
 */
export type LineDecision =
  | { kind: 'create' }
  | { kind: 'reconcile'; transactionId: string }
  | {
      kind: 'reconcile_invoice';
      cardAccountId: string;
      periodStart: string;
      periodEnd: string;
      transactionIds: string[];
    }
  /**
   * Cria uma transferência entre contas (do `accountId` para a conta destino).
   * Útil quando o OFX traz uma linha que é, na verdade, transferência (TED, DOC, PIX
   * entre contas próprias) e não despesa/receita. O sinal do TRNAMT define o sentido:
   * débito → sai da conta selecionada para a destino; crédito → vem da destino para a
   * selecionada. O wizard troca origem/destino conforme `type` da linha.
   */
  | { kind: 'create_transfer'; destinationAccountId: string };

export interface InvoiceCandidate {
  cardAccountId: string;
  cardAccountName: string;
  periodStart: string;
  periodEnd: string;
  total: number;
  transactionIds: string[];
}

export interface ReconcileCandidate {
  transactionId: string;
  description: string;
  dueDate: Date;
  amount: number;
}

export interface ImportState {
  step: 1 | 2 | 3 | 4 | 5;
  importType: ImportType;
  accountId: string;
  /** Apenas para importType='transferir': conta destino das transferências. */
  destinationAccountId?: string;
  fileName?: string;
  parseResult?: ParseResult;
  categoryMappings: CategoryMapping[];
  costCenterMappings: CostCenterMapping[];
  lineDecisions?: LineDecision[];
  importResult?: ImportResult;
}

export interface ImportResult {
  transactionsCreated: number;
  transactionsReconciled: number;
  transactionsSkippedDuplicate: number;
  categoriesCreated: number;
  costCentersCreated: number;
  errors: { line?: number; reason: string }[];
}

export function typeForImport(importType: ImportType): TransactionType {
  if (importType === 'receber') return 'income';
  if (importType === 'transferir') return 'transfer';
  // 'pagar' e 'fatura-cartao' são despesas
  return 'expense';
}

/** True se o ParseResult vem de OFX (todas as linhas têm type definido). */
export function isOfxResult(parseResult: ParseResult | undefined): boolean {
  if (!parseResult || parseResult.rows.length === 0) return false;
  return parseResult.rows.every((r) => r.type !== undefined);
}
